import type { ArrowFunction, LC } from '@use-gpu/live';
import type { StorageSource, Lazy, UniformAttribute, VectorLike, VolatileAllocation } from '@use-gpu/core';
import type { ShaderModule } from '@use-gpu/shader';

import { Ref, yeet, useMemo, useOne, SUSPEND } from '@use-gpu/live';

import { useDeviceContext } from '../providers/device-provider';
import { useSuspenseContext } from '../providers/suspense-provider';

import {
  makeMultiUniforms, makeBoundUniforms, makeVolatileUniforms,
  uploadBuffer,
  resolve,
} from '@use-gpu/core';
import { useLinkedShader } from '../hooks/useLinkedShader';
import { useComputePipelineAsync } from '../hooks/useComputePipeline';
import { useInspectable } from '../hooks/useInspectable'
import { usePipelineLayout } from '../hooks/usePipelineLayout';

export type DispatchProps = {
  size?: Lazy<number[] | VectorLike>,
  group?: Lazy<number[] | VectorLike>,
  indirect?: StorageSource | null,

  shader: ShaderModule,

  globalLayout?: GPUBindGroupLayout | null,
  globalBinding?: (pipeline: GPURenderPipeline) => VolatileAllocation,
  globalDefs?: UniformAttribute[][],
  globalUniforms?: Record<string, Ref<any>>,

  pipelineKey?: string | number,
  customLayouts?: GPUBindGroupLayout[],

  shouldDispatch?: () => boolean | number | null | undefined,
  onDispatch?: () => void,

  defines?: Record<string, any>,
  label?: string,
};

const NO_SIZE = [1];
const NO_CALL: Record<string, ArrowFunction> = {};

const GLOBAL_DEFINES = {
  '@group(PASS)': '@group(0)',
  '@group(VIRTUAL)': '@group(1)',
  '@group(VOLATILE)': '@group(2)',
  '@group(CUSTOM)': '@group(3)',
};

const LOCAL_DEFINES = {
  '@group(VIRTUAL)': '@group(0)',
  '@group(VOLATILE)': '@group(1)',
  '@group(CUSTOM)': '@group(2)',
  '@group(LOCAL)': '@group(3)',
};

export const Dispatch: LC<DispatchProps> = (props: DispatchProps) => {
  // Return a lambda back to parent(s)
  return yeet(dispatch(props));
};

// Inlined into <Component>
export const dispatch = (props: DispatchProps) => {
  const {
    size = NO_SIZE,
    group,
    indirect,
    shader: computeShader,

    globalLayout,
    globalBinding,
    globalDefs,
    globalUniforms,

    pipelineKey,
    customLayouts,

    defines: propDefines,
    shouldDispatch,
    onDispatch,
    label,
  } = props;

  const inspect = useInspectable();

  // Dispatch set up
  const device = useDeviceContext();
  const suspense = useSuspenseContext();

  // Defines
  const hasGlobals = !!(globalLayout ?? globalBinding ?? globalDefs);
  const defines = useMemo(() => (propDefines ? {
    ...(hasGlobals ? GLOBAL_DEFINES : LOCAL_DEFINES),
    ...propDefines,
  } : (hasGlobals ? GLOBAL_DEFINES : LOCAL_DEFINES)), [propDefines, hasGlobals]);

  // Shader
  const {
    shader: [module],
    fields,
    constants,
    bindings,
    volatiles,
    entries,
  } = useLinkedShader(
    [computeShader],
    defines,
    label,
  );

  // Pipeline layout with global bind group and optional pass-specific bind group
  const layout = usePipelineLayout(device, entries, globalLayout, customLayouts, label);

  // Compute pipeline
  const [pipeline, isStale] = useComputePipelineAsync(device, module, layout, label);
  if (!pipeline) return suspense ? SUSPEND : NO_CALL;
  if (isStale) return SUSPEND;

  // @Group(n)
  const base = +!!hasGlobals;

  // Uniforms
  const uniform = useMemo(() => {
    if (globalLayout) return null;
    if (globalBinding) return globalBinding(pipeline);
    if (globalDefs) return makeMultiUniforms(device, pipeline, globalDefs, 0);
    return null;
  }, [device, pipeline, globalLayout, globalBinding, globalDefs]);

  // Bound storage
  const force = !!volatiles.length;
  const storage = useMemo(() =>
    makeBoundUniforms(device, pipeline, fields, bindings, base, force),
    [device, pipeline, fields, bindings, force]);

  // Volatile storage
  const volatile = useMemo(() =>
    makeVolatileUniforms(device, pipeline, volatiles, base + 1),
    [device, pipeline, volatiles]
  );

  const inspected = inspect({
    render: {
      workgroups: 0,
      version: null,
    },
    indirect,
  });

  const isVolatileGlobal = typeof uniform?.bindGroup === 'function';

  let dispatchVersion: number | null = null;

  const compute = (passEncoder: GPUComputePassEncoder, countDispatch: (w: number, s: number) => void) => {
    onDispatch && onDispatch();

    const s = resolve(size ?? NO_SIZE);
    const m = resolve(group ?? null);

    let sx = s[0] || 1;
    let sy = s[1] || 1;
    let sz = s[2] || 1;

    const n = sx * sy * sz;
    if (m) {
      sx = Math.ceil(sx / m[0]);
      sy = Math.ceil(sy / (m[1] || 1));
      sz = Math.ceil(sz / (m[2] || 1));
    }
    const w = sx * sy * sz;

    inspected.render.samples = m ? n : 0;
    inspected.render.workgroups = w;
    inspected.render.version = dispatchVersion;
    countDispatch(w, m ? n : 0);

    /*
    const bs = [];
    for (const b of bindings) if (b.storage) bs.push(b.storage.version);
    for (const b of volatiles) if (b.storage) bs.push(b.storage.version);
    console.log('dispatch', computeShader.module.name, bs)
    */

    if (uniform) {
      if (globalUniforms) {
        (uniform as any).pipe.fill(globalUniforms);
        uploadBuffer(device, (uniform as any).buffer, (uniform as any).pipe.data);
      }

      if (isVolatileGlobal) passEncoder.setBindGroup(0, (uniform as any).bindGroup());
      else passEncoder.setBindGroup(0, (uniform as any).bindGroup);
    }

    if (storage.pipe && storage.buffer) {
      storage.pipe.fill(constants);
      uploadBuffer(device, storage.buffer, storage.pipe.data);
    }

    passEncoder.setPipeline(pipeline);
    if (storage.bindGroup) passEncoder.setBindGroup(base, storage.bindGroup);
    if (volatile.bindGroup) passEncoder.setBindGroup(base + 1, volatile.bindGroup());

    if (indirect) passEncoder.dispatchWorkgroupsIndirect(indirect.buffer, indirect.byteOffset ?? 0);
    else {
      passEncoder.dispatchWorkgroups(sx, sy, sz);
    }
  };

  if (shouldDispatch) {
    return {
      compute: (passEncoder: GPUComputePassEncoder, countDispatch: (d: number) => void) => {
        const d = shouldDispatch();
        if (d === false) return;
        if (typeof d === 'number') {
          if (dispatchVersion === d) return;
          dispatchVersion = d;
        }

        return compute(passEncoder, countDispatch);
      },
    };
  }

  return {compute};
};
